<template>
<v-dialog v-model="showReminder" max-width="420">
    <v-card title="Your trial ends soon">
        <v-card-text>
            Your FolderFeed trial expires in less than a day. After that you'll switch to the Free plan with limited folders, files and daily syncs.
            <router-link to="/pricing" @click="showReminder = false">Switch to Pro</router-link>
            to keep everything unlimited.
        </v-card-text>

        <v-card-actions>
            <v-spacer />

            <v-btn variant="text" @click="showReminder = false">
                Close
            </v-btn>
        </v-card-actions>
    </v-card>
</v-dialog>

<v-dialog v-model="showEnded" max-width="420">
    <v-card title="Your trial has ended">
        <v-card-text>
            You're now on the Free plan: up to {{ limits.maxFolders }} monitored folders, the latest {{ limits.maxFiles }} files per folder and {{ limits.dailySyncLimit }} syncs per day.
            <router-link to="/pricing" @click="showEnded = false">Switch to Pro</router-link>
            to remove these limits.
        </v-card-text>

        <v-card-actions>
            <v-spacer />

            <v-btn variant="text" @click="showEnded = false">
                Close
            </v-btn>
        </v-card-actions>
    </v-card>
</v-dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { FREE_PLAN_LIMITS as limits } from '@/config/plan'
import { usePlanStore } from '@/stores/plan'
import { useRegistryStore } from '@/stores/registry'
import { logger } from '@/utils/logger'

const TRIAL_REMINDER_KEY = 'trialReminderDismissed'
const TRIAL_ENDED_KEY = 'trialEndedDismissed'

const route = useRoute()
const planStore = usePlanStore()
const registryStore = useRegistryStore()

const showReminder = ref(false)
const showEnded = ref(false)

// Dialogs are shown while the user navigates between the Folder/File pages
function isFolderOrFileRoute (name: string) {
    return name.startsWith('/folders/') || name.startsWith('/d/')
}

async function evaluate () {
    if (showReminder.value || showEnded.value || !isFolderOrFileRoute(String(route.name))) return

    try {
        if (planStore.trialEndingSoon) {
            if (!(await registryStore.getEntry(TRIAL_REMINDER_KEY))?.value) {
                showReminder.value = true
            }
        } else if (planStore.isFree && !planStore.hasHadSubscription && planStore.trialEndsAt !== null && !(await registryStore.getEntry(TRIAL_ENDED_KEY))?.value) {
            showEnded.value = true
        }
    } catch (error) {
        logger.error('Failed to evaluate plan dialogs:', error)
    }
}

// Closing the dialog in any way (button, link, Esc, click outside) counts as dismissed
watch(showReminder, open => {
    if (!open) registryStore.setEntry(TRIAL_REMINDER_KEY, true)
})

watch(showEnded, open => {
    if (!open) registryStore.setEntry(TRIAL_ENDED_KEY, true)
})

watch(() => [route.name, planStore.plan, planStore.trialEndingSoon], evaluate, { immediate: true })
</script>
